import { useSyncExternalStore } from "react"
import { getVoximplantClient } from "@/lib/voice/voximplant-client"
import type { ConnectionStatus } from "@/types/v2/voice"

export interface SoftphoneConnection {
  status: ConnectionStatus
  error: string | null
}

// Cached so `useSyncExternalStore`'s getSnapshot returns a
// stable reference until status/error actually change — a fresh
// object on every render would loop. The Voximplant client is a
// module-level singleton, so a module-level cache is safe.
let cached: SoftphoneConnection = { status: "idle", error: null }

function readConnection(): SoftphoneConnection {
  const { connection, connectionError } = getVoximplantClient().getState()
  if (connection !== cached.status || connectionError !== cached.error) {
    cached = { status: connection, error: connectionError }
  }
  return cached
}

// Live softphone connection state (status + humane error).
//
// Drives the Call affordance's gating: a call may only start
// once the SDK has actually connected. Gating on the server
// `voiceConfig.enabled` alone (which only means a credential
// row exists) let a failed/still-connecting softphone fire a
// doomed call that rang an agent leg that never answered —
// leaving a zombie "already on a call" with no error (CON-167).
export function useSoftphoneConnection(): SoftphoneConnection {
  const client = getVoximplantClient()
  return useSyncExternalStore(client.subscribe, readConnection, () => cached)
}
